import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Loader2, Star, Mail, Phone, Building2, Tag, Search, Save, X, Edit3, User } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactsPanel({ api }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ceo-life/contacts', { params: { q: search } });
      setContacts(data || []);
    } catch { toast.error('Failed to load contacts'); }
    setLoading(false);
  }, [api, search]);

  useEffect(() => {
    const t = setTimeout(fetchContacts, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchContacts, search]);

  const startCreate = () => { setCreating(true); setEditing(true); setActive(null); setDraft(emptyDraft()); };
  const open = (c) => { setActive(c); setCreating(false); setEditing(false); setDraft({ ...c, tags: c.tags || [] }); };
  const startEdit = () => { setEditing(true); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    try {
      if (draft.id) {
        const { data } = await api.put(`/ceo-life/contacts/${draft.id}`, draft);
        setContacts(prev => prev.map(c => c.id === data.id ? data : c).sort((a,b)=>a.name.localeCompare(b.name)));
        setActive(data);
        toast.success('Updated');
      } else {
        const { data } = await api.post('/ceo-life/contacts', draft);
        setContacts(prev => [...prev, data].sort((a,b)=>a.name.localeCompare(b.name)));
        setActive(data);
        setCreating(false);
        toast.success('Contact added');
      }
      setEditing(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const toggleFavorite = async (c) => {
    try {
      const { data } = await api.put(`/ceo-life/contacts/${c.id}`, { favorite: !c.favorite });
      setContacts(prev => prev.map(x => x.id === data.id ? data : x));
      if (active?.id === c.id) setActive(data);
    } catch { toast.error('Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/ceo-life/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      if (active?.id === id) { setActive(null); setEditing(false); }
    } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }}/></div>;

  const favorites = contacts.filter(c => c.favorite);
  const others = contacts.filter(c => !c.favorite);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="ceo-contacts" style={{ minHeight: 480 }}>
      {/* List */}
      <div className="md:col-span-5 space-y-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-low)' }} />
            <input className="ceo-input" style={{ paddingLeft: 32 }} placeholder="Search by name, org, email..." value={search} onChange={e=>setSearch(e.target.value)} data-testid="ceo-contacts-search" />
          </div>
          <button onClick={startCreate} className="ceo-btn ceo-btn-primary" data-testid="ceo-contact-new"><Plus className="h-3.5 w-3.5" /></button>
        </div>

        <div className="ceo-card-elevated overflow-hidden" style={{ maxHeight: 540, overflowY: 'auto' }}>
          {contacts.length === 0 ? (
            <div className="ceo-empty m-2 border-0 p-6">
              <User className="ceo-empty-icon h-8 w-8" />
              <p className="text-xs">{search ? 'No matches.' : 'Build your rolodex. Add a stakeholder to begin.'}</p>
            </div>
          ) : (
            <>
              {favorites.length > 0 && (
                <>
                  <div className="px-4 py-2 ceo-eyebrow flex items-center gap-1.5" style={{ background: 'var(--ink-3)' }}>
                    <Star className="h-2.5 w-2.5" style={{ color: 'var(--gold)' }} fill="currentColor"/>Favorites
                  </div>
                  {favorites.map(c => <ContactRow key={c.id} c={c} active={active?.id === c.id} onClick={() => open(c)} />)}
                </>
              )}
              {others.length > 0 && (
                <>
                  {favorites.length > 0 && <div className="px-4 py-2 ceo-eyebrow" style={{ background: 'var(--ink-3)' }}>All contacts</div>}
                  {others.map(c => <ContactRow key={c.id} c={c} active={active?.id === c.id} onClick={() => open(c)} />)}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Detail */}
      <div className="md:col-span-7">
        {(active || creating) ? (
          editing ? (
            <ContactForm
              draft={draft}
              setDraft={setDraft}
              onCancel={() => { setEditing(false); setCreating(false); if (active) setDraft({ ...active, tags: active.tags || [] }); }}
              onSave={save}
            />
          ) : (
            <ContactView
              c={active}
              onEdit={startEdit}
              onDelete={() => remove(active.id)}
              onToggleFavorite={() => toggleFavorite(active)}
            />
          )
        ) : (
          <div className="ceo-empty h-full flex flex-col items-center justify-center">
            <User className="ceo-empty-icon h-10 w-10" />
            <p className="text-sm" style={{ color: 'var(--text-dim)' }}>Pick a contact, or hit <strong style={{ color: 'var(--text)' }}>+</strong> to add someone new.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ContactRow({ c, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 block transition-colors"
      style={{
        background: active ? 'var(--ink-3)' : 'transparent',
        borderBottom: '1px solid var(--line)',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
      }}
      data-testid={`ceo-contact-${c.id}`}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--accent-2), var(--accent))', color: '#0F0F18' }}
        >
          {(c.name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>{c.name}</span>
            {c.favorite && <Star className="h-3 w-3 shrink-0" style={{ color: 'var(--gold)' }} fill="currentColor"/>}
          </div>
          <div className="text-[11px] truncate" style={{ color: 'var(--text-dim)' }}>
            {c.role}{c.role && c.organization ? ' · ' : ''}{c.organization}
          </div>
        </div>
      </div>
    </button>
  );
}

function ContactView({ c, onEdit, onDelete, onToggleFavorite }) {
  return (
    <div className="ceo-card-elevated p-6 space-y-4" data-testid="ceo-contact-view">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold"
            style={{ background: 'linear-gradient(135deg, var(--accent-2), var(--accent))', color: '#0F0F18' }}
          >
            {(c.name || '?').charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="ceo-headline text-2xl">{c.name}</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-dim)' }}>
              {c.role}{c.role && c.organization ? ' · ' : ''}{c.organization}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onToggleFavorite} className="ceo-btn ceo-btn-ghost" title={c.favorite ? 'Unfavorite' : 'Add to favorites'} data-testid="ceo-contact-favorite">
            <Star className="h-3.5 w-3.5" style={{ color: c.favorite ? 'var(--gold)' : 'var(--text-low)' }} fill={c.favorite ? 'currentColor' : 'none'}/>
          </button>
          <button onClick={onEdit} className="ceo-btn ceo-btn-ghost" data-testid="ceo-contact-edit"><Edit3 className="h-3 w-3" />Edit</button>
          <button onClick={onDelete} className="ceo-btn ceo-btn-danger" data-testid="ceo-contact-delete"><Trash2 className="h-3 w-3" /></button>
        </div>
      </div>

      <div className="ceo-divider" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {c.email && <Field icon={Mail} label="Email" value={<a href={`mailto:${c.email}`} className="ceo-link-quiet">{c.email}</a>} />}
        {c.phone && <Field icon={Phone} label="Phone" value={<a href={`tel:${c.phone}`} className="ceo-link-quiet">{c.phone}</a>} />}
        {c.organization && <Field icon={Building2} label="Organization" value={c.organization} />}
        {(c.tags || []).length > 0 && <Field icon={Tag} label="Tags" value={(c.tags || []).map(t => <span key={t} className="ceo-pill" style={{ marginRight: 4, fontSize: 10 }}>#{t}</span>)} />}
      </div>

      {c.notes && (
        <div>
          <div className="ceo-eyebrow mb-2">Notes</div>
          <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)', lineHeight: 1.6 }}>{c.notes}</p>
        </div>
      )}
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="ceo-eyebrow flex items-center gap-1.5 mb-1"><Icon className="h-2.5 w-2.5" />{label}</div>
      <div className="text-sm" style={{ color: 'var(--text)' }}>{value}</div>
    </div>
  );
}

function ContactForm({ draft, setDraft, onCancel, onSave }) {
  const set = (k, v) => setDraft(p => ({ ...p, [k]: v }));
  return (
    <div className="ceo-card-elevated p-5 space-y-3" data-testid="ceo-contact-form">
      <h3 className="ceo-headline text-xl">{draft.id ? 'Edit contact' : 'New contact'}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="ceo-eyebrow">Name *</label>
          <input className="ceo-input" value={draft.name || ''} onChange={e => set('name', e.target.value)} data-testid="ceo-contact-name" autoFocus />
        </div>
        <div>
          <label className="ceo-eyebrow">Role / Title</label>
          <input className="ceo-input" value={draft.role || ''} onChange={e => set('role', e.target.value)} data-testid="ceo-contact-role" />
        </div>
        <div>
          <label className="ceo-eyebrow">Organization</label>
          <input className="ceo-input" value={draft.organization || ''} onChange={e => set('organization', e.target.value)} data-testid="ceo-contact-org" />
        </div>
        <div>
          <label className="ceo-eyebrow">Email</label>
          <input className="ceo-input" type="email" value={draft.email || ''} onChange={e => set('email', e.target.value)} data-testid="ceo-contact-email" />
        </div>
        <div>
          <label className="ceo-eyebrow">Phone</label>
          <input className="ceo-input" type="tel" value={draft.phone || ''} onChange={e => set('phone', e.target.value)} data-testid="ceo-contact-phone" />
        </div>
        <div>
          <label className="ceo-eyebrow">Tags (comma separated)</label>
          <input className="ceo-input" value={(draft.tags || []).join(', ')} onChange={e => set('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} data-testid="ceo-contact-tags" />
        </div>
      </div>
      <div>
        <label className="ceo-eyebrow">Notes</label>
        <textarea rows={4} className="ceo-textarea" placeholder="Background, preferences, last meeting..." value={draft.notes || ''} onChange={e => set('notes', e.target.value)} data-testid="ceo-contact-notes" />
      </div>
      <div className="flex items-center justify-between pt-2">
        <label className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-dim)' }}>
          <input type="checkbox" className="ceo-checkbox" checked={!!draft.favorite} onChange={e => set('favorite', e.target.checked)} data-testid="ceo-contact-fav-checkbox" />
          Mark as favorite
        </label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="ceo-btn ceo-btn-ghost" data-testid="ceo-contact-cancel"><X className="h-3 w-3" />Cancel</button>
          <button onClick={onSave} className="ceo-btn ceo-btn-primary" data-testid="ceo-contact-save"><Save className="h-3 w-3" />{draft.id ? 'Update' : 'Add contact'}</button>
        </div>
      </div>
    </div>
  );
}

function emptyDraft() {
  return { name: '', role: '', organization: '', email: '', phone: '', tags: [], notes: '', favorite: false };
}
